import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Timer, ChevronRight } from "lucide-react";
import api, { imgUrl } from "../lib/api";
import { Reels } from "./GameFX";
import { haptic } from "../lib/haptics";
import { fmtLeft } from "./LuckyLive";
import { useMT } from "../lib/miniI18n";
import { useAuth } from "../context/AuthContext";
import { celebrateWin } from "../lib/celebrate";
import { fmt } from "../lib/format";
import { themeFor } from "../lib/gameTheme";
import { RoyalStage } from "./RoyalStage";
import { playRevealSound, soundKindFor } from "../lib/revealSound";

const hm = (s) => String(s || "00:00").split(":").map(Number);
const mins = (s) => { const [h, m] = hm(s); return h * 60 + m; };
const pad = (n) => String(n).padStart(2, "0");
const to12 = (t) => { const [h, m] = hm(t); return `${((h + 11) % 12) + 1}:${pad(m)} ${h < 12 ? "AM" : "PM"}`; };

/* Home: every fixed show drawing at the SAME hour, together — one countdown, each show keeps its own colours/emblem, numbers reveal in place. Includes every fixed show so players see the full lineup. */
export function ResultHourLineup({ games, catImages = {}, onSettled }) {
  const navigate = useNavigate();
  const mt = useMT();
  const { user } = useAuth();
  const [now, setNow] = useState(Date.now());
  const [results, setResults] = useState({}); // game_id -> {number, won}
  const [mine, setMine] = useState({}); // game_id -> count of my numbers today
  const [glow, setGlow] = useState(false);
  const polled = useRef(0);
  const ref = useRef(null);
  const mountedAt = useRef(Date.now());
  const location = useLocation();

  useEffect(() => {
    if (!user) { setMine({}); return; }
    api.get("/bets/my", { params: { tab: "current" } }).then((r) => {
      const m = {};
      for (const b of r.data || []) m[b.game_id] = (m[b.game_id] || 0) + (b.quantity || 1);
      setMine(m);
    }).catch(() => {});
  }, [user, location.key]);

  useEffect(() => {
    if (!new URLSearchParams(location.search).get("lineup")) return;
    const t = setTimeout(() => { ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }); setGlow(true); setTimeout(() => setGlow(false), 6000); }, 400);
    return () => clearTimeout(t);
  }, [location.search, games.length]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const SPIN_WINDOW = 75; // seconds after a show's draw time during which the reels may spin — later visitors only see the number
  const istSec = (ms) => { const d = new Date(ms + 5.5 * 3600000); return d.getUTCHours() * 3600 + d.getUTCMinutes() * 60 + d.getUTCSeconds(); };
  const ist = new Date(now + 5.5 * 3600000);
  const nowSec = istSec(now);
  const mountSec = istSec(mountedAt.current);
  const today = `${ist.getUTCFullYear()}-${pad(ist.getUTCMonth() + 1)}-${pad(ist.getUTCDate())}`;
  const all = games.filter((g) => g.is_active !== false && g.mode !== "interval" && g.draw_time);
  // shows are grouped into a block (draw times within 15 min of the first); the block stays until 6 min after its last draw (15 min while a number is still missing)
  const times = [...new Set(all.map((g) => g.draw_time))].sort((a, b) => mins(a) - mins(b));
  let block = [];
  for (let i = 0; i < times.length; i++) {
    const grp = times.filter((t) => mins(t) >= mins(times[i]) && mins(t) < mins(times[i]) + 15);
    const lastSec = mins(grp[grp.length - 1]) * 60;
    const pending = all.some((g) => grp.includes(g.draw_time) && !results[g.id]);
    if (nowSec < lastSec + (pending ? 15 : 6) * 60) { block = grp; break; }
    i += grp.length - 1;
  }
  const shows = block.length ? all.filter((g) => block.includes(g.draw_time)).sort((a, b) => mins(a.draw_time) - mins(b.draw_time) || (a.sort_order || 0) - (b.sort_order || 0)) : [];
  const slot = block[0];
  const drawSec = (g) => mins(g.draw_time) * 60;
  const nextWaiting = shows.find((g) => nowSec < drawSec(g));
  const secs = nextWaiting ? drawSec(nextWaiting) - nowSec : 0;
  const phase = !slot ? "none" : shows.every((g) => nowSec < drawSec(g)) ? "waiting" : "reveal";
  // reels may spin only right at the draw (first 75 s) and only if the page was already open — anyone arriving later sees the plain number
  const canSpin = (g) => nowSec - drawSec(g) <= SPIN_WINDOW && mountSec <= drawSec(g);

  useEffect(() => {
    if (phase !== "reveal" || !shows.length) return;
    const pending = shows.filter((g) => nowSec >= drawSec(g) && !results[g.id]);
    if (!pending.length || now - polled.current < 2000) return;
    polled.current = now;
    (async () => {
      let won = [];
      if (user) { try { const { data } = await api.get("/bets/my", { params: { tab: "won" } }); won = data || []; } catch {} }
      let all_res = [];
      try { const { data } = await api.get("/results", { params: { date: today, limit: 300 } }); all_res = data || []; } catch { return; }
      const found = {};
      for (const g of pending) {
        const r = all_res.find((x) => x.game_id === g.id && x.draw_date === today && (x.draw_time === g.draw_time || !x.draw_time));
        if (!r) continue;
        const w = won.filter((b) => b.game_id === g.id && b.draw_date === today && b.status === "won").reduce((a, b) => a + (b.win_amount || 0), 0);
        const isLate = !canSpin(g) || Date.now() - mountedAt.current < 1500; // result already existed / draw moment has passed → no fake spin
        found[g.id] = { number: r.winning_number, won: w, at: Date.now(), late: isLate, spinning: !isLate, pass: Array.isArray(r.pattern_pass) ? r.pattern_pass : [] };
      }
      if (!Object.keys(found).length) return;
      setResults((p) => ({ ...p, ...found }));
      const fresh = Object.entries(found).filter(([, v]) => v.spinning);
      if (!fresh.length) { onSettled && onSettled(); return; }
      // spin the reels on the real number for 2.6 s, THEN reveal + celebrate + let the rest of the page update
      setTimeout(() => {
        setResults((p) => { const n = { ...p }; for (const [id] of fresh) if (n[id]) n[id] = { ...n[id], spinning: false, at: Date.now() }; return n; });
        haptic.land();
        const fg = shows.find((g) => g.id === fresh[0][0]);
        if (fg) { const th = themeFor(fg); playRevealSound(th.key, soundKindFor(th)); }
        for (const [, v] of fresh) if (v.won > 0) celebrateWin(v.won, 0);
        onSettled && onSettled();
      }, 2600);
    })();
  }, [now, phase]); // eslint-disable-line

  if (!slot || !shows.length) return null;
  return (
    <div ref={ref} className={`rounded-2xl transition-shadow ${glow ? "rl-lineup-glow" : ""}`} data-testid="result-hour-lineup" data-glow={glow ? "1" : "0"}>
    <RoyalStage game={shows.length === 1 ? shows[0] : undefined} image={shows.length === 1 ? catImages[shows[0].category] : undefined} drawTime={slot} className="rounded-2xl p-3 shadow-lg" dense>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs text-white/80"><Timer className="w-4 h-4 text-amber-300" />{phase === "waiting" ? mt("Result at") : mt("Results")} <b className="text-white">{to12(slot)}{block.length > 1 ? `–${to12(block[block.length - 1])}` : ""}</b><span className="text-white/50">· {shows.length} {mt(shows.length === 1 ? "show" : "shows")}</span></div>
        {phase === "waiting" ? <span className="font-mono font-black text-amber-300 text-xl drop-shadow" data-testid="lineup-countdown">{fmtLeft(secs)}</span> : <span className="text-[10px] font-black text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full animate-pulse" data-testid="lineup-live">● LIVE</span>}
      </div>
      <div className={`grid gap-2 ${shows.length === 1 || phase === "reveal" ? "grid-cols-1" : "grid-cols-2"}`} data-testid="lineup-grid" data-cols={shows.length === 1 || phase === "reveal" ? "1" : "2"}>
        {shows.map((g) => {
          const th = themeFor(g);
          const r = results[g.id];
          const waiting = nowSec < drawSec(g);
          const spinning = !waiting && canSpin(g) && (!r || r.spinning);
          const late = !waiting && !canSpin(g);
          const digits = Math.max(4, String(g.last_result?.winning_number || "").length);
          return (
            <button key={g.id} onClick={() => navigate(`/play/${g.id}`)} className={`relative rounded-2xl p-2.5 text-left text-white overflow-hidden active:scale-[0.98] transition-transform border border-white/15 ${mine[g.id] > 0 ? "ring-2 ring-emerald-400" : ""}`} style={{ background: shows.length === 1 ? "rgba(255,255,255,.10)" : th.bg, backdropFilter: shows.length === 1 ? "blur(6px)" : undefined }} data-vibe={th.key} data-testid={`lineup-show-${g.id}`} data-state={waiting ? "waiting" : spinning ? "spinning" : r ? "result" : "awaited"}>
              <span className="absolute -right-4 -top-4 w-20 h-20 rounded-full blur-2xl" style={{ background: th.glow }} />
              {catImages[g.category] && <img src={imgUrl(catImages[g.category])} alt="" aria-hidden className="absolute -right-3 -bottom-3 w-20 h-20 rounded-full object-cover opacity-30 blur-[1px] pointer-events-none" style={{ maskImage: "radial-gradient(circle, #000 35%, transparent 72%)", WebkitMaskImage: "radial-gradient(circle, #000 35%, transparent 72%)" }} />}
              <span className="absolute right-1 bottom-0 text-3xl opacity-25 pointer-events-none select-none" aria-hidden>{th.motif}</span>
              {mine[g.id] > 0 && <span className="absolute top-1.5 right-1.5 text-[9px] font-black bg-emerald-400 text-emerald-950 rounded-full px-1.5 py-0.5 shadow" data-testid={`lineup-booked-${g.id}`}>✓ {mt("You booked")} · {mine[g.id]}</span>}
              <div className="flex items-center gap-2">
                {catImages[g.category] ? <img src={imgUrl(catImages[g.category])} alt="" className="w-9 h-9 rounded-lg object-cover rl-gold-border" /> : <div className="w-9 h-9 rounded-lg rl-gold-bg flex items-center justify-center font-display font-black text-slate-900 text-sm">{g.category?.[0]}</div>}
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-xs leading-tight truncate">{g.name}</div>
                  <div className="text-[9px] text-white/70 uppercase tracking-wider">{mt("Result")} {to12(g.draw_time)}</div>
                </div>
              </div>
              <div className="mt-2 min-h-[34px] flex items-center justify-between">
                {waiting ? (
                  <>
                    <span className="text-[10px] text-white/80">{g.runtime?.is_open ? mt("Booking open") : mt("Booking closed")}{phase === "reveal" ? <> · <span className="font-mono font-bold text-amber-200" data-testid={`lineup-show-countdown-${g.id}`}>{fmtLeft(drawSec(g) - nowSec)}</span></> : null}</span>
                    {g.runtime?.is_open && <span className="rl-gold-bg text-[9px] font-black px-2 py-0.5 rounded-full flex items-center">{mt("BOOK")}<ChevronRight className="w-3 h-3" /></span>}
                  </>
                ) : r && !r.spinning ? (
                  <div className={`w-full min-w-0 ${late || r.late ? "" : "rl-reveal"}`} data-testid={`lineup-result-${g.id}`} data-late={late || r.late ? "1" : "0"}>
                    <div className="flex items-center justify-between gap-1"><span className="text-[10px] text-amber-200 truncate">{mt("Result")}</span><span className="font-mono font-black text-lg tracking-wider text-amber-300 drop-shadow whitespace-nowrap shrink-0" data-testid={`lineup-number-${g.id}`}>{r.number}</span></div>
                    {r.pass?.length > 0 && <div className="mt-1 text-[9px] font-black text-emerald-100 bg-emerald-700/70 rounded-full px-2 py-0.5 w-fit" data-testid={`lineup-pass-${g.id}`}>✓ {mt("Pattern passed on")} {r.pass.join(" · ")}</div>}
                    {r.won > 0 && <div className="mt-1 text-[10px] font-black bg-emerald-500 rounded-full px-2 py-0.5 w-fit" data-testid={`lineup-won-${g.id}`}>🏆 {mt("YOU WON")} {fmt(r.won)}</div>}
                  </div>
                ) : spinning ? (
                  <div className="w-full flex items-center justify-between gap-2 min-w-0" data-testid={`lineup-spinning-${g.id}`}>
                    <span className="text-[10px] text-amber-200 animate-pulse shrink-0">{mt("Drawing…")}</span>
                    {(() => { const sc = 0.58; const w = (digits * 56 + (digits - 1) * 8 + 26) * sc; const h = 142 * sc; return (
                      <span className="shrink-0 overflow-hidden" style={{ width: w, height: h }}><span className="block origin-top-left" style={{ transform: `scale(${sc})` }}><Reels value={r?.number || 0} spinning digits={digits} title={`${g.name} · ${to12(g.draw_time)}`} /></span></span>
                    ); })()}
                  </div>
                ) : (
                  <span className="text-[10px] text-white/70" data-testid={`lineup-awaited-${g.id}`}>{mt("Result awaited")}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </RoyalStage>
    </div>
  );
}
